import React from "react";
import { RegulatoryLicence } from "../../../../types/cdd-records.types";
import { formatDateShort } from "../../../../utils/dateFormatters";
import { licenceTypeLabels } from "./entityProfileConfig";

export const LicenceCard: React.FC<{ licence: RegulatoryLicence }> = ({
  licence,
}) => {
  const typeInfo =
    licenceTypeLabels[licence.licenceType] || licenceTypeLabels["OTHER"];

  const isExpired =
    licence.expiryDate && new Date(licence.expiryDate) < new Date();
  const isExpiringSoon =
    licence.expiryDate &&
    !isExpired &&
    new Date(licence.expiryDate) <
      new Date(Date.now() + 90 * 24 * 60 * 60 * 1000);

  return (
    <div
      className={`bg-white rounded-lg shadow-sm border ${
        licence.status === "current"
          ? "border-gray-200"
          : licence.status === "suspended"
            ? "border-yellow-300"
            : "border-red-300"
      } overflow-hidden`}
    >
      {/* Header */}
      <div
        className={`px-6 py-4 ${
          licence.status === "current"
            ? "bg-blue-50"
            : licence.status === "suspended"
              ? "bg-yellow-50"
              : "bg-red-50"
        }`}
      >
        <div className="flex items-start justify-between">
          <div>
            <div className="flex items-center gap-2">
              <span className="text-lg font-bold text-gray-900">
                {licence.licenceType}
              </span>
              <span
                className={`px-2 py-0.5 text-xs font-medium rounded ${
                  licence.status === "current"
                    ? "bg-green-100 text-green-800"
                    : licence.status === "suspended"
                      ? "bg-yellow-100 text-yellow-800"
                      : licence.status === "cancelled"
                        ? "bg-red-100 text-red-800"
                        : "bg-gray-100 text-gray-800"
                }`}
              >
                {licence.status}
              </span>
            </div>
            <p className="text-sm text-gray-600 mt-1">{typeInfo.label}</p>
          </div>
          <div className="text-right">
            <span className="text-xs text-gray-500">Licence Number</span>
            <div className="font-mono text-sm font-medium text-gray-900">
              {licence.licenceNumber}
            </div>
          </div>
        </div>
      </div>

      {/* Body */}
      <div className="px-6 py-4">
        <dl className="grid grid-cols-2 gap-4">
          <div>
            <dt className="text-xs text-gray-500">Licence Holder</dt>
            <dd className="text-sm font-medium text-gray-900">
              {licence.licenceHolderName}
            </dd>
          </div>
          <div>
            <dt className="text-xs text-gray-500">Issue Date</dt>
            <dd className="text-sm text-gray-900">
              {formatDateShort(licence.issueDate || "")}
            </dd>
          </div>
          <div>
            <dt className="text-xs text-gray-500">Expiry Date</dt>
            <dd className="text-sm text-gray-900 flex items-center gap-2">
              {formatDateShort(licence.expiryDate || "")}
              {isExpired && (
                <span className="px-1.5 py-0.5 text-xs bg-red-100 text-red-800 rounded">
                  Expired
                </span>
              )}
              {isExpiringSoon && (
                <span className="px-1.5 py-0.5 text-xs bg-orange-100 text-orange-800 rounded">
                  Expiring Soon
                </span>
              )}
            </dd>
          </div>
        </dl>

        {/* Conditions */}
        {licence.conditions && licence.conditions.length > 0 && (
          <div className="mt-4">
            <h5 className="text-xs font-semibold text-gray-500 uppercase mb-2">
              Conditions
            </h5>
            <ul className="space-y-1">
              {licence.conditions.map((condition, index) => (
                <li
                  key={index}
                  className="flex items-start gap-2 text-sm text-gray-700"
                >
                  <svg
                    className="w-4 h-4 text-orange-500 mt-0.5 flex-shrink-0"
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z"
                    />
                  </svg>
                  {condition}
                </li>
              ))}
            </ul>
          </div>
        )}

        {/* Authorisations */}
        {licence.authorisations && licence.authorisations.length > 0 && (
          <div className="mt-4">
            <h5 className="text-xs font-semibold text-gray-500 uppercase mb-2">
              Authorisations
            </h5>
            <div className="flex flex-wrap gap-2">
              {licence.authorisations.map((auth, index) => (
                <span
                  key={index}
                  className="px-2 py-1 bg-blue-50 text-blue-700 rounded text-xs"
                >
                  {auth}
                </span>
              ))}
            </div>
          </div>
        )}
      </div>
    </div>
  );
};
